import React, { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import ButtonBase from "@mui/material/ButtonBase";
import { Link, useMatch, useResolvedPath } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { GetAllInvoicesData, GetPendingEnforcementIntentsData } from "common/graphql";
import Fade from "@mui/material/Fade";
import { Theme } from "@mui/material/styles";
import Badge from "@mui/material/Badge";

export const DesktopNavBarItem: React.FC<DesktopNavItemProps> = ({
  title,
  to,
  linkType,
  onClick,
  icon,
  invoicesData,
  invoicesLoading,
  pendingEnforcementIntentsData,
  pendingPermitsCount,
}) => {
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end: false });
  const isInvoicesDropDownMenu = to === "/invoices";
  const isEnforcement = to === "/notices";
  const isPermits = to === "/permits";
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const invoicesSubMenuOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const handleInvoicesSubMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleInvoicesSubMenuClose = () => {
    setAnchorEl(null);
  };
  const handleInvoicesSubMenuItemClicked = (vehicleId: string | undefined) => {
    if (vehicleId === undefined) {
      return;
    }
    handleInvoicesSubMenuClose();
    navigate(`/vehicles/${vehicleId}/invoices`);
  };

  const invoices = [...(invoicesData?.invoices || [])];
  const unpaidInvoicesCount = invoices.filter((invoice) => invoice.amount_paid === 0 && !invoice.voided_at).length;
  invoices.sort((invoiceA, invoiceB) => {
    if (invoiceA.amount_paid === 0) {
      return -1;
    }
    if (invoiceB.amount_paid === 0) {
      return 1;
    }
    return 0;
  });

  const badgeContent = isEnforcement
    ? pendingEnforcementIntentsData?.enforcementIntentAggregate.aggregate.count
    : isPermits
    ? pendingPermitsCount || undefined
    : isInvoicesDropDownMenu
    ? unpaidInvoicesCount || undefined
    : undefined;

  return (
    <Badge badgeContent={badgeContent} color="primary" max={99}>
      <ButtonBase
        href={to}
        to={to}
        component={linkType === "external" ? ("a" as const) : Link}
        target={linkType === "external" ? "_blank" : undefined}
        sx={(theme: Theme) => ({
          display: "inline-flex",
          px: 1,
          pt: 1.25,
          pb: 1,
          ":hover": {
            bgcolor: theme.palette.grey[100],
          },
          ...(match && {
            borderBottom: 2,
            borderBottomColor: "primary.main",
          }),
          color: (theme: Theme) => (title === "Logout" ? theme.palette.primary.main : theme.palette.text.primary),
        })}
        id={isInvoicesDropDownMenu && invoicesSubMenuOpen ? "fade-button" : to}
        aria-controls={isInvoicesDropDownMenu && invoicesSubMenuOpen ? "fade-menu" : undefined}
        aria-haspopup={isInvoicesDropDownMenu && invoicesSubMenuOpen ? "true" : "false"}
        aria-expanded={isInvoicesDropDownMenu && invoicesSubMenuOpen ? "true" : undefined}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          if (isInvoicesDropDownMenu) {
            e.preventDefault();
            handleInvoicesSubMenuClick(e);
          } else if (onClick) {
            e.preventDefault();
            onClick(e);
          }
        }}
      >
        {icon && !isInvoicesDropDownMenu && (
          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
            {icon}
            <Typography sx={{ ml: 1, fontWeight: 600 }} variant="h7small">
              {title}
            </Typography>
          </Box>
        )}
        {(!icon || isInvoicesDropDownMenu) && (
          <Typography variant="h7small" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        {isInvoicesDropDownMenu && <KeyboardArrowDownIcon sx={{ ml: 1.5 }} />}
      </ButtonBase>
      {isInvoicesDropDownMenu && invoices.length !== 0 && !invoicesLoading && (
        <Menu
          id="fade-menu"
          MenuListProps={{
            "aria-labelledby": "fade-button",
          }}
          sx={{
            ".MuiList-root": {
              maxHeight: "210px",
              py: "10px",
            },
            ".MuiMenuItem-root": {
              display: "flex",
              justifyContent: "space-between",
            },
          }}
          anchorEl={anchorEl}
          open={invoicesSubMenuOpen}
          onClose={handleInvoicesSubMenuClose}
          TransitionComponent={Fade}
        >
          {invoices.map((invoice) => (
            <MenuItem
              key={invoice.id}
              onClick={() => handleInvoicesSubMenuItemClicked(invoice.registered_vehicle.id)}
              sx={{
                pr: 2,
              }}
            >
              <Typography variant="body1" mr={"52px"}>
                {String(invoice.index).padStart(6, "0")}
              </Typography>
              {invoice.amount_paid === 0 && !invoice.voided_at && (
                <Typography variant="caption" color="#E31B0C">
                  Unpaid
                </Typography>
              )}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Badge>
  );
};

export interface DesktopNavItemProps {
  title: string;
  to: string;
  linkType?: "external" | "profileLink";
  onClick?: (e: React.SyntheticEvent) => void;
  icon?: ReactNode;
  invoicesData?: GetAllInvoicesData;
  invoicesLoading?: boolean;
  pendingEnforcementIntentsData?: GetPendingEnforcementIntentsData;
  pendingEnforcementIntentsLoading?: boolean;
  pendingPermitsCount?: number;
}

export default DesktopNavBarItem;
